// src/data/offPropertyLetters.js
//
// THE OFF-PROPERTY LETTERS - the appeal to the next officer, and the notice of intent to the Board.
// The THIRD outbound letter of this flow, the presentment notice, already exists as
// letterGroup.presentmentLetterGroup and is not restated here (§XI).
//
// Ruling 177 / master list 3.1, ISS-39. Taken under PD-19, withdrawable by number.
//
// ---------------------------------------------------------------------------------------------
// THESE LETTERS GET THE SAME TREATMENT AS EVERY OTHER LETTER ON THIS PLATFORM (Bob, explicit).
//
//   - They state ONLY what is on the record: the claim's own fields, the dates and provisions of the
//     recorded acts, and the carrier's OWN stated grounds - each resolving to its source. Nothing is
//     composed about the merits. This module writes no prose of its own about the case; every line is
//     (a) a claim field, (b) a recorded act's date/party, (c) rule text handed in verbatim by the
//     caller through ruleResolver, or (d) fixed connective wording that asserts nothing.
//   - They NEVER fabricate a citation, an award, or a deadline. This is the same discipline that makes
//     appealLadder refuse the carrier clock: an absent thing prints [NOT ON FILE] and holds `sendable`
//     false; it is never filled from memory. "No award on file" would read as a gap in the index, not
//     a finding that none exists - so no such sentence is generated at all.
//   - The body is a DRAFT the officer reviews and ADOPTS. It carries the DRAFT stamp, it is never
//     presented as final authority, and NOTHING here sends anything. Sending is the officer's own act
//     (own-assets-only: the platform records the carrier as it reasons; it never probes or targets the
//     carrier official).
//   - Identity and sign-off come from the GC letterhead capability (letterGroup.js): the three-block
//     header resolved from platform data, the parent International named at its head (ruling 188), and
//     the committee's own signature block (ruling 110) - never typed, never guessed.
//
// Pure: no React, no Firestore. The caller reads the documents and hands them in.

import { signatureBlock } from './appealSigner';   // master list item 4: the one who submits signs
import { letterHeader, resolveContactBlocks, NOT_ON_FILE, PARENT_ORGANIZATION } from './letterGroup';
import { lastOf } from './appealLadder';
import { claimActs } from './offProperty';
import { getClaimRequirements } from './claimRequirements';

function nonEmpty(v) { return v !== null && v !== undefined && String(v).trim() !== ''; }
function field(claim, key, label, missing) {
  const v = claim ? claim[key] : undefined;
  if (!nonEmpty(v)) { missing.push(label); return NOT_ON_FILE; }
  return String(v).trim();
}

// The DRAFT stamp every off-property letter carries at head and foot - it is a draft for the officer
// to adopt and send, never an auto-sent or final document (Bob, explicit; the DRAFT REASONING stamp).
const DRAFT_HEAD =
  'DRAFT - FOR THE OFFICER TO REVIEW AND ADOPT. Not sent by the platform. It states only what is on\n'
+ 'the record; read it against the claim before it goes to the carrier, and sign and send it yourself.';
const FOOT =
  '\n\nEvery statement above is taken from the claim record and the agreement catalogue as filed on this\n'
+ 'platform; nothing is added, paraphrased, or inferred (data\\YCP_EVIDENTIARY_STANDARD_2026-08-23.md).\n'
+ 'This is a DRAFT. The platform did not send it.';

// The rules the claim rests on, verbatim, handed in resolved by the caller (ruleResolver) - this
// module never composes rule language (PD-16). Mirrors letterGroup.rulesSection.
function rulesSection(claim, ruleTexts, missing) {
  const relied = (Array.isArray(claim && claim.matchedRules) ? claim.matchedRules : []).filter(r => r && !r.setAsideAt);
  if (!relied.length) { missing.push('rule relied on'); return 'RULE(S) RELIED ON\n' + NOT_ON_FILE; }
  return 'RULE(S) RELIED ON\n' + relied.map(r => {
    const t = (ruleTexts || []).find(x => x && x.rule === r.rule) || null;
    if (!t || !nonEmpty(t.text)) { missing.push('text of ' + (r.rule || r.ruleId)); return (r.rule || r.ruleId) + '\n' + NOT_ON_FILE + ' - the catalogue holds no text for this rule'; }
    return (r.rule || r.ruleId) + (t.status && t.status !== 'active' ? ' [' + t.status + ']' : '') + '\n"' + t.text.trim() + '"';
  }).join('\n\n');
}

// The recorded course of the claim off the property, as DATES AND PARTIES only - each line is a
// recorded act, nothing is characterised. An empty history is stated as empty, never embellished.
function historySection(claim) {
  const acts = claimActs(claim);
  if (!acts.length) return 'RECORD OF HANDLING\n' + NOT_ON_FILE + ' - no acts are recorded off the property.';
  const line = (a) => {
    const spec = { presented: 'Presented on the carrier', carrier_answered: 'Carrier answered', appealed: 'Appealed to the next officer', conference_requested: 'Conference requested', conference_held: 'Conference held', conference_refused: 'Conference refused', board_notice: 'Notice of intent to the Board', board_submitted: 'Submitted to the Board', award_issued: 'Award issued', award_complied: 'Award complied with', award_not_complied: 'Award not complied with' };
    let s = '  ' + a.on + '  ' + (spec[a.kind] || a.kind);
    if (a.kind === 'carrier_answered' && nonEmpty(a.outcome)) s += ' (' + a.outcome.replace(/_/g, ' ') + ')';
    if (a.kind === 'appealed' && nonEmpty(a.toOfficer)) s += ' - ' + a.toOfficer;
    return s;
  };
  return 'RECORD OF HANDLING\n' + acts.map(line).join('\n');
}

// The carrier's OWN stated grounds, from the latest carrier answer - sourced as the carrier's
// statement (ruling 28: it proves what the carrier said, not that it is true), resolving to the
// stored declination if one is linked. Never the platform's characterisation of the denial.
function carrierGroundsSection(claim) {
  const ans = lastOf(claimActs(claim), 'carrier_answered');
  if (!ans) return null;
  const lines = ['THE CARRIER’S STATED GROUNDS (the carrier’s own words, as recorded - not this office’s characterisation)'];
  if (nonEmpty(ans.reasonGiven)) {
    lines.push('"' + String(ans.reasonGiven).trim() + '"');
    lines.push(ans.declinationDocPath
      ? 'Source: the carrier’s declination on file (' + (ans.declinationDocName || ans.declinationDocPath) + ').'
      : 'Source: recorded from the carrier’s answer of ' + ans.on + '; no declination document is attached.');
  } else {
    lines.push(NOT_ON_FILE + ' - the carrier’s answer is recorded but its stated grounds were not captured.');
  }
  return lines.join('\n');
}

function contactBlocksOrResolve(claim, ctx) {
  return (ctx && ctx.blocks) ? ctx.blocks : resolveContactBlocks(claim, ctx || {});
}

// ---------------------------------------------------------------------------------------------
// THE APPEAL TO THE NEXT OFFICER - CLAIM-successive-officer-handling.
//
// `toOfficer` is who it is appealed to. It comes from an already-recorded `appealed` act if there is
// one, else from the caller. It is never invented - with none, the letter is not sendable and says so.
export function appealLetter(claim, ctx, ruleTexts) {
  const c = ctx || {};
  const missing = [];
  const blocks = contactBlocksOrResolve(claim, c);
  const hdr = letterHeader(blocks);
  const number = field(claim, 'id', 'claim number', missing);
  const claimant = field(claim, 'claimantName', 'claimant', missing);

  const appealedAct = lastOf(claimActs(claim), 'appealed');
  const toOfficer = nonEmpty(c.toOfficer) ? String(c.toOfficer).trim()
    : (appealedAct && nonEmpty(appealedAct.toOfficer) ? appealedAct.toOfficer : '');
  if (!nonEmpty(toOfficer)) missing.push('the officer this appeal is directed to');

  // An appeal answers a denial. If none is recorded, that is a gap the officer must see - you do not
  // appeal an answer that is not on the record.
  const denial = lastOf(claimActs(claim), 'carrier_answered');
  if (!denial) missing.push('the carrier answer this appeal responds to');

  const grounds = carrierGroundsSection(claim);
  const unresolvedBlocks = ['generalCommittee', 'localCommittee', 'railroad'].filter(k => blocks[k] && !blocks[k].resolved).map(k => blocks[k].kind);

  const parts = [
    DRAFT_HEAD, '', hdr, '',
    'TO: ' + (nonEmpty(toOfficer) ? toOfficer : NOT_ON_FILE),
    'RE: APPEAL - CLAIM ' + number + ' (' + claimant + ')', '',
    'This office appeals the disposition of the above claim to you as the next officer, under the',
    'grievance procedure of the controlling agreement (CLAIM-successive-officer-handling).', '',
    historySection(claim),
  ];
  if (grounds) parts.push('', grounds);
  parts.push('', rulesSection(claim, ruleTexts, missing));
  parts.push('', 'The claim as presented is not withdrawn. This office asks that it be sustained.');
  // Master list item 4: signed by the one who submits it - the caller's signer, else the recorded appeal's.
  const appealSign = signatureBlock(c.signer || (appealedAct && appealedAct.signedBy));
  if (appealSign) parts.push('', appealSign); else missing.push('the officer submitting and signing this appeal');
  parts.push(FOOT);

  const uniq = Array.from(new Set(missing));
  return {
    ok: true, id: 'APPEAL', title: 'Appeal of claim ' + number + ' to the next officer',
    to: toOfficer || null, text: parts.join('\n'),
    unresolvedBlocks, missingFields: uniq,
    sendable: unresolvedBlocks.length === 0 && uniq.length === 0,
  };
}

// ---------------------------------------------------------------------------------------------
// THE NOTICE OF INTENT TO THE BOARD - CLAIM-joint-submission-preparation / -ex-parte-submission.
//
// A NOTICE, not the submission itself. It states the claim, its recorded handling, and the intent to
// submit to the NAMED board. The board and the submission kind are never invented; with neither, the
// notice is not sendable and says which is missing.
export function boardNoticeLetter(claim, ctx, ruleTexts) {
  const c = ctx || {};
  const missing = [];
  const blocks = contactBlocksOrResolve(claim, c);
  const hdr = letterHeader(blocks);
  const number = field(claim, 'id', 'claim number', missing);
  const claimant = field(claim, 'claimantName', 'claimant', missing);

  const board = nonEmpty(c.board) ? String(c.board).trim() : '';
  if (!nonEmpty(board)) missing.push('the Board this notice names');
  const submissionKind = nonEmpty(c.submissionKind) ? String(c.submissionKind).trim() : '';
  const kindLine = submissionKind === 'joint' ? 'by joint submission'
    : submissionKind === 'ex_parte' ? 'by ex parte submission'
    : (missing.push('whether the submission is joint or ex parte'), NOT_ON_FILE);

  // The Board proceeding runs on a clock in the agreement (the time to institute proceedings). Read it
  // verbatim if the agreement carries one; never assert one it does not.
  const req = getClaimRequirements(claim && claim.applicableAgreement);
  const boardClock = ((req && req.clocks) || []).find(k => k && /board|institute|proceedings|subsequent handling/i.test(k.label || ''));

  const unresolvedBlocks = ['generalCommittee', 'localCommittee', 'railroad'].filter(k => blocks[k] && !blocks[k].resolved).map(k => blocks[k].kind);

  const parts = [
    DRAFT_HEAD, '', hdr, '',
    'RE: NOTICE OF INTENT TO SUBMIT TO ' + (nonEmpty(board) ? board.toUpperCase() : NOT_ON_FILE),
    'CLAIM ' + number + ' (' + claimant + ')', '',
    'This office gives notice of its intent to submit the above claim to ' + (nonEmpty(board) ? board : NOT_ON_FILE)
      + ' ' + kindLine + '.', '',
    historySection(claim),
    '', rulesSection(claim, ruleTexts, missing),
  ];
  if (boardClock) {
    parts.push('', 'THE AGREEMENT’S CLOCK ON THIS STEP (verbatim, ' + (boardClock.cite || 'cite not on file') + ')');
    parts.push('  ' + boardClock.label + ': ' + (boardClock.days ? boardClock.days + ' days' : NOT_ON_FILE)
      + (boardClock.from ? ' from ' + boardClock.from : '') + (boardClock.onSilence ? ' - on silence, "' + boardClock.onSilence + '"' : ''));
  }
  // Master list item 4: signed by the one who submits it.
  const noticeAct = lastOf(claimActs(claim), 'board_notice');
  const boardSign = signatureBlock(c.signer || (noticeAct && noticeAct.signedBy));
  if (boardSign) parts.push('', boardSign); else missing.push('the officer submitting and signing this notice');
  parts.push(FOOT);

  const uniq = Array.from(new Set(missing));
  return {
    ok: true, id: 'BOARD_NOTICE', title: 'Notice of intent to the Board - claim ' + number,
    to: board || null, text: parts.join('\n'),
    unresolvedBlocks, missingFields: uniq,
    sendable: unresolvedBlocks.length === 0 && uniq.length === 0,
  };
}

export { PARENT_ORGANIZATION };
